import { Body1, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";
import { ArrowRight16Regular, Checkmark16Regular, Copy16Regular } from "@fluentui/react-icons";
import { useLayoutEffect, useMemo, useRef, useState, type ReactNode, type RefObject } from "react";
import { useNavigate } from "react-router-dom";
import { FuiButton } from "@/components/fui-control";
import { SectionTitle } from "@/components/ui-typography";
import { deltaSense, Metric, MetricPanel } from "@/components/metric-panel";
import { CheckList, type CheckListItem } from "@/components/visual/CheckList";
import { SERIES } from "@/components/visual/chart-colors";
import { TrackRow } from "@/components/visual/ink";
import { axisBarsHeight, BarsVisual, ColumnsVisual, HeatmapVisual, PhaseProgressVisual, StackedBar } from "@/components/visual/charts";
import { monthOf, projectCostMonths, projectCostSummary, projectMonthlyBuckets } from "@/features/resources/portfolio";
import { ReportVisual } from "@/components/visual/ReportVisual";
import { toDay, addDays, todayISO } from "@/engine/date";
import type { ExecutionTask, ProjectRecord } from "@/engine/types";
import { openExecutionTask, openScopeInSchedule } from "@/features/tasks/links";
import { AssigneeAvatars } from "@/features/tasks/task-ui";
import { displayBaselineName, fmtCurrency, fmtDelta } from "@/lib/format";
import { formatDate, formatDateRange } from "@/lib/date-format";
import { useProjectStore } from "@/store/project-store";
import { STATUS_LABEL } from "@/store/execution-task-ops";
import { isRecordOverdue, recordPhase, summarizeRecords } from "@/store/record-ops";
import { CostEvmStrip } from "@/features/pm/CostEvmStrip";
import { statusIssueTracks } from "./status-tracks";
import {
  buildNarrative,
  CHANGE_CATEGORIES,
  CHANGE_CATEGORY_HINT,
  CHANGE_CATEGORY_LABEL,
  CHANGE_KIND_LABEL,
  countByCategory,
  diffReportContexts,
  narrativeToText,
  reportChecks,
  type ChangeCategory,
  type ChangeItem,
} from "./report-model";
import {
  burnPct,
  changeCategoryBars,
  changeSpotlights,
  changeTable,
  costCompositionBars,
  costCompositionTable,
  costMonthModel,
  costParts,
  formatCostAxis,
  periodAssigneeCounts,
  formatPhasePoints,
  phaseDistributionLine,
  phaseMovers,
  phaseProgressBars,
  phaseProgressTable,
  type CostPartKey,
  type PhaseMover,
  weekDueChart,
  weekDueMatrix,
} from "./report-charts";
import { SegmentedControl } from "@/features/resources/SegmentedControl";
import { useMoneyMasked } from "@/features/access/use-plan-access";
import { pageMax, pageStackAt } from "@/shell/content-frame";
import { fmtChangeValue, fmtSignedNum, fmtSignedPp, SourceBadge, useReportPoints, useReportStyles } from "./report-ui";

const useStyles = makeStyles({
  mast: { display: "flex", alignItems: "center", justifyContent: "space-between", gap: tokens.spacingHorizontalL, minWidth: 0 },
  mastMain: { display: "flex", alignItems: "baseline", gap: tokens.spacingHorizontalM, minWidth: 0, flex: "1 1 auto" },
  mastTitle: { display: "flex", alignItems: "center", gap: tokens.spacingHorizontalS, minWidth: 0, flexShrink: 1 },
  ellipsis: { overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0 },
  mastMeta: { fontSize: tokens.fontSizeBase200, lineHeight: tokens.lineHeightBase200, color: tokens.colorNeutralForeground3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", minWidth: 0, flex: "1 1 auto" },
  band: { paddingTop: tokens.spacingVerticalS, borderTop: `1px solid ${tokens.colorNeutralStroke2}` },
  stackSection: { display: "flex", flexDirection: "column", gap: tokens.spacingVerticalM, minWidth: 0 },
  subBlock: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalXS,
    paddingTop: tokens.spacingVerticalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    minWidth: 0,
  },
  subTitle: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground2,
  },
  fill: { height: "100%" },
  pair: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalM,
    alignItems: "stretch",
    [pageMax(pageStackAt)]: { gridTemplateColumns: "1fr" },
  },
  help: { display: "flex", flexDirection: "column", gap: "4px", margin: 0 },
  helpLead: { margin: 0 },
  helpList: { margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "2px" },
  helpItem: { margin: 0 },
  emptyLine: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    minWidth: 0,
  },
  /** 확인할 일·늦은 작업·마일스톤·담당별 마감. 두 열에서 네 칸이 같은 높이가 된다. */
  board: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalM,
    alignItems: "stretch",
    minWidth: 0,
    [pageMax(pageStackAt)]: { gridTemplateColumns: "1fr" },
  },
  periodMeta: { display: "inline-flex", alignItems: "center", gap: tokens.spacingHorizontalS, flexShrink: 0, minWidth: 0 },
  spanDate: { flexShrink: 0, color: tokens.colorNeutralForeground3, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  note: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalM,
    padding: `${tokens.spacingVerticalS} ${tokens.spacingHorizontalM}`,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px dashed ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  noteActions: { display: "flex", gap: tokens.spacingHorizontalS, flexShrink: 0 },
  list: { display: "flex", flexDirection: "column" },
  movers: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: tokens.spacingHorizontalM,
    minWidth: 0,
  },
  moverCol: { display: "flex", flexDirection: "column", gap: "2px", minWidth: 0 },
  moverHeadLine: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    gap: tokens.spacingHorizontalS,
    minWidth: 0,
  },
  moverHead: {
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
    color: tokens.colorNeutralForeground3,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  moverHeadGrow: { flex: "1 1 auto", minWidth: 0 },
  moverRow: {
    display: "grid",
    gridTemplateColumns: "minmax(0, 1fr) auto",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
    minHeight: "28px",
    padding: `0 ${tokens.spacingHorizontalXS}`,
    margin: `0 -${tokens.spacingHorizontalXS}`,
    borderRadius: tokens.borderRadiusSmall,
    border: "none",
    background: "none",
    font: "inherit",
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground1,
    textAlign: "left",
    width: `calc(100% + ${tokens.spacingHorizontalXS} * 2)`,
    boxSizing: "border-box",
  },
  moverButton: {
    cursor: "pointer",
    ":hover": { backgroundColor: tokens.colorNeutralBackground1Hover },
    ":active": { backgroundColor: tokens.colorNeutralBackground1Pressed },
    ":focus-visible": { outline: `2px solid ${tokens.colorStrokeFocus2}`, outlineOffset: "-2px" },
  },
});

interface Row {
  key: string;
  /** 왼쪽 칸. WBS 코드 또는 이슈·막힘 같은 종류 */
  tag?: string;
  /** 왼쪽 칸에 코드 대신 두는 표시. 담당 얼굴 등 */
  leading?: ReactNode;
  name: string;
  /** 이름에 올렸을 때. 역할처럼 이름 옆에서 잘리는 설명 */
  hint?: string;
  value: ReactNode;
  taskId?: string;
  /** 현재 시점의 이슈 행. 누르면 이슈 드로어가 열린다 */
  recordId?: string;
  /** 실행 Task 행. 누르면 Task 드로어가 열린다 */
  execId?: string;
}

export function ReportStatusView({ atKey, onOpenChanges, onClosePeriod, onCreateDemo }: { atKey: string; onOpenChanges: (category?: ChangeCategory) => void; onClosePeriod?: () => void; onCreateDemo?: () => void }) {
  const s = useReportStyles();
  const st = useStyles();
  const boardRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const points = useReportPoints();
  const { masked, canReveal } = useMoneyMasked();
  const activeId = useProjectStore((x) => x.activeId);
  const liveTaskIds = useProjectStore((x) => x.project.tasks);
  /** 7일 마감 막대·주별 칸으로 고른 날. 보고 시점이 바뀌면 따라가지 않는다. */
  const [pickedDay, setPickedDay] = useState<{ key: string; date: string } | null>(null);
  const [dueView, setDueView] = useState<"days" | "weeks">("weeks");
  const [copied, setCopied] = useState(false);

  const point = points.byKey.get(atKey) ?? points.current;
  const prevPoint = points.previousOf(point.key);
  const ctx = points.ctx(point.key)!;
  const prev = prevPoint ? points.ctx(prevPoint.key) : null;
  const m = ctx.metrics;
  const pm = prev?.metrics;

  const items = useMemo(() => (prev ? diffReportContexts(prev, ctx, points.resources) : []), [prev, ctx, points.resources]);
  const counts = useMemo(() => countByCategory(items), [items]);
  const live = new Set(liveTaskIds.map((t) => t.id));
  const isCurrent = point.kind === "current";
  const jump = (r: Row) => {
    const { taskId, recordId, execId } = r;
    if (recordId) return () => useProjectStore.getState().setRecordFocus(recordId);
    if (execId && activeId && isCurrent) return () => openExecutionTask(activeId, execId);
    return taskId && activeId && live.has(taskId) ? () => openScopeInSchedule(activeId, taskId) : undefined;
  };

  const periodFrom = pm ? addDays(pm.statusDate, 1) : m.projectStart;
  // 첫 보고는 프로젝트 시작이 기준일보다 늦을 수 있다. 거꾸로 된 기간 대신 기준일만 적는다.
  const periodLabel = periodFrom > m.statusDate ? formatDate(m.statusDate, "numeric") : formatDateRange(periodFrom, m.statusDate, "numeric");

  const nameOf = (id: string) => ctx.project.tasks.find((t) => t.id === id)?.name ?? "—";
  const delayRows: Row[] = ctx.delays.map((d) => {
    const pct = ctx.schedule.tasks.get(d.taskId)?.percentComplete ?? 0;
    return {
      key: d.taskId,
      tag: d.wbs,
      name: d.name,
      hint: d.critical ? `핵심경로 · 진척 ${Math.round(pct)}%` : `진척 ${Math.round(pct)}%`,
      value: `${d.critical ? "핵심 · " : ""}+${d.varianceDays}일`,
      taskId: d.taskId,
    };
  });
  const statusDay = toDay(m.statusDate);
  const milestoneRows: Row[] = ctx.schedule.order
    .map((id) => ctx.schedule.tasks.get(id)!)
    .filter((t) => t.isMilestone && t.percentComplete < 100 && toDay(t.start) >= statusDay - 7)
    .map((t) => ({ key: t.id, tag: t.wbs, name: nameOf(t.id), value: formatDate(t.start, "short"), taskId: t.id }));
  const blocked = ctx.project.executionTasks.filter((x) => x.status === "blocked");
  const overdueTasks = ctx.project.executionTasks.filter((x) => x.status !== "done" && x.status !== "blocked" && !!x.due && x.due < m.statusDate);
  // 이슈: 스냅샷(마감한 기간)은 그 시점의 기록으로 센다. 기한 지남의 기준일도 그 시점의 진척 기준일이다.
  const recordDay = isCurrent ? todayISO() : m.statusDate;
  const records = ctx.project.records ?? [];
  const recSummary = summarizeRecords(records, recordDay);
  const issueRows: Row[] = records
    .filter((r) => (r.kind === "issue" || r.kind === "risk") && recordPhase(r.status) === "open")
    .sort((a, b) => Number(isRecordOverdue(b, recordDay)) - Number(isRecordOverdue(a, recordDay)) || Number(b.severity === "high") - Number(a.severity === "high") || (a.due ?? "9999").localeCompare(b.due ?? "9999"))
    .map((r) => {
      const late = isRecordOverdue(r, recordDay);
      return { key: r.id, tag: r.kind === "risk" ? "리스크" : "이슈", name: r.title || "제목 없음", value: r.due ? formatDate(r.due, "short") : late ? "기한 지남" : "기한 없음", recordId: isCurrent ? r.id : undefined };
    });
  const checks = reportChecks(ctx, recordDay).map((c) => {
    if (c.key !== "budget" || !masked) return c;
    return {
      ...c,
      status: "na" as const,
      figure: "—",
      aside: undefined,
      mark: canReveal ? "단가를 숨긴 상태" : "금액 조회 권한이 필요합니다",
      text: "",
      rule: undefined,
    };
  });
  const checkItems: CheckListItem[] = checks.map((c) => ({
    key: c.key,
    label: c.label,
    status: c.status,
    figure: c.figure,
    aside: c.aside,
    mark: c.mark,
    text: c.text,
    rule: c.rule,
    open:
      c.go && isCurrent && activeId
        ? {
            label: c.goLabel ?? `${c.label} 열기`,
            onClick: () => navigate(`/projects/${activeId}/${c.go}`),
          }
        : undefined,
  }));

  // 이슈·리스크와 막힌 Task를 먼저 배치해 기한 초과 건수에 가려지지 않게 한다.
  const actionRows: Row[] = [
    ...issueRows,
    ...blocked.map((x) => ({ key: x.id, tag: "막힘", name: x.title || "제목 없는 Task", value: x.due ? formatDate(x.due, "short") : "기한 없음", execId: x.id })),
    ...overdueTasks.map((x) => ({ key: x.id, tag: "초과", name: x.title || "제목 없는 Task", value: x.due ? formatDate(x.due, "short") : "기한 없음", execId: x.id })),
  ];
  const openRecords = recSummary.openIssues + recSummary.openRisks;
  const attention = openRecords + blocked.length + overdueTasks.length;
  const prevAttention = useMemo(() => {
    if (!prev || !pm) return null;
    const prevRec = summarizeRecords(prev.project.records ?? [], pm.statusDate);
    return prevRec.openIssues + prevRec.openRisks + pm.execBlocked + pm.execOverdue;
  }, [prev, pm]);

  const progressDiff = pm ? m.progress - pm.progress : null;
  const finishShiftDays = pm ? ctx.calendar.ordinalBeforeISO(m.projectFinish) - ctx.calendar.ordinalBeforeISO(pm.projectFinish) : null;
  const burnDiffPp = pm && !masked && m.budget > 0 && pm.budget > 0 ? burnPct(m) - burnPct(pm) : null;
  const attentionDiff = prevAttention !== null ? attention - prevAttention : null;

  const narrative = useMemo(() => buildNarrative(ctx, prev, items, masked), [ctx, prev, items, masked]);
  const copySummary = () => {
    const text = narrativeToText(ctx.project.name, periodLabel, checks, narrative);
    void navigator.clipboard?.writeText(text);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  };

  const byStatus = (status: string) => ctx.project.executionTasks.filter((x) => x.status === status).length;
  const taskSegments = [
    { key: "done" as const, label: "완료", value: byStatus("done"), color: SERIES.good },
    { key: "inProgress" as const, label: "진행 중", value: byStatus("inProgress"), color: SERIES.actual },
    { key: "blocked" as const, label: "막힘", value: byStatus("blocked"), color: SERIES.bad },
    { key: "notStarted" as const, label: "시작 전", value: byStatus("notStarted"), color: SERIES.empty },
  ];
  const phaseBars = useMemo(
    () =>
      phaseProgressBars(ctx, {
        onPick: isCurrent && activeId ? (id) => openScopeInSchedule(activeId, id) : undefined,
      }),
    [ctx, isCurrent, activeId],
  );
  const prevPhaseBars = useMemo(() => (prev ? phaseProgressBars(prev) : []), [prev]);
  const phaseLine = phaseDistributionLine(phaseBars);
  const movers = prev ? phaseMovers(phaseBars, prevPhaseBars) : null;
  const showMovers = Boolean(movers && (movers.gains.length > 0 || movers.stalls.length > 0));
  const changeTotal = items.length;
  const changeBars = changeCategoryBars(counts, { onPick: (category) => onOpenChanges(category) });
  const periodTasks = ctx.project.executionTasks
    .filter((x) => !!x.due && x.due >= periodFrom && x.due <= m.statusDate)
    .sort((a, b) => (a.due ?? "").localeCompare(b.due ?? "") || a.title.localeCompare(b.title, "ko"));
  const picked = pickedDay?.key === atKey ? pickedDay.date : null;
  const week = useMemo(
    () =>
      weekDueChart(
        { statusDate: m.statusDate, tasks: ctx.project.executionTasks },
        points.periods.map((p) => ({
          key: p.key,
          label: formatDate(p.project.statusDate, "short"),
          statusDate: p.project.statusDate,
          tasks: p.project.executionTasks,
        })),
        {
          isWorkingDay: (iso) => ctx.calendar.isWorkingISO(iso),
          earliest: periodFrom,
          previousKey: prevPoint?.key,
          highlightDate: picked,
          onPick: (date) => setPickedDay((cur) => (cur?.key === atKey && cur.date === date ? null : { key: atKey, date })),
        },
      ),
    [atKey, ctx, m.statusDate, periodFrom, picked, points.periods, prevPoint?.key],
  );
  const matrix = useMemo(
    () =>
      weekDueMatrix(
        { statusDate: m.statusDate, tasks: ctx.project.executionTasks },
        {
          earliest: periodFrom,
          onPick: (date) => setPickedDay((cur) => (cur?.key === atKey && cur.date === date ? null : { key: atKey, date })),
        },
      ),
    [atKey, ctx.project.executionTasks, m.statusDate, periodFrom],
  );
  const dueDay = picked && week.dates.includes(picked) ? picked : null;
  const shownPeriodTasks = dueDay ? periodTasks.filter((task) => task.due === dueDay) : periodTasks;
  const resourceOf = (name: string) => Object.values(points.resources).find((r) => r.displayName === name);
  const costLinked = isCurrent && !!activeId;
  const cost = useMemo(() => {
    const months = projectCostMonths(ctx.project, ctx.schedule.projectFinish);
    const buckets = projectMonthlyBuckets(ctx.project, points.resources, months);
    const summary = projectCostSummary(ctx.project, points.resources);
    const openMonth = costLinked ? () => navigate(`/projects/${activeId}/budget?view=timeline`) : undefined;
    const openPart = costLinked
      ? (key: CostPartKey) => navigate(`/projects/${activeId}/budget?view=${key === "solution" ? "solutions" : "breakdown"}`)
      : undefined;
    const parts = costParts(summary);
    return {
      month: costMonthModel(buckets, { budget: m.budget, statusMonth: monthOf(m.statusDate), masked, onPick: openMonth }),
      bars: costCompositionBars(parts, { masked, onPick: openPart }),
      table: costCompositionTable(parts, { masked, onPick: openPart }),
    };
  }, [activeId, costLinked, ctx.project, ctx.schedule.projectFinish, m.budget, m.statusDate, masked, navigate, points.resources]);
  const assigneeRows: Row[] = periodAssigneeCounts(periodTasks).map((row) => {
    const unset = row.key === "none";
    const role = unset ? undefined : resourceOf(row.name)?.roleTitle;
    return {
      key: row.key,
      leading: <AssigneeAvatars names={unset ? [] : [row.name]} size={20} />,
      name: row.name,
      hint: role,
      value: `${row.count}건`,
    };
  });
  const openAssignee = (row: Row) =>
    isCurrent && activeId ? () => navigate(`/projects/${activeId}/tasks?assignee=${encodeURIComponent(row.key === "none" ? "" : row.name)}`) : undefined;
  useEqualCardHeight(boardRef, `${atKey}:${actionRows.length}:${delayRows.length}:${milestoneRows.length}:${assigneeRows.length}`);
  const metaLine = `${point.kind === "current" ? "작성 중" : "마감"} · ${periodLabel}${ctx.baseline ? ` · 비교 계획 ${displayBaselineName(ctx.baseline.name)}` : " · 비교할 계획 없음"}`;
  const title = point.kind === "current" ? ctx.project.name : point.label;
  return (
    <div className={s.wrap}>
      {points.periods.length === 0 && (
        <div className={st.note} role="note">
          <Body1 className={s.secondary}>아직 마감한 보고 기간이 없어요. 기간을 마감하면 다음 보고부터 지난 마감과 견줄 수 있어요.</Body1>
          {(onCreateDemo || onClosePeriod) && (
            <div className={st.noteActions}>
              {onCreateDemo && (
                <FuiButton appearance="subtle" onClick={onCreateDemo}>
                  예시 기간 만들기
                </FuiButton>
              )}
              {onClosePeriod && (
                <FuiButton appearance="secondary" onClick={onClosePeriod}>
                  첫 기간 마감
                </FuiButton>
              )}
            </div>
          )}
        </div>
      )}

      <section className={s.card} aria-label="보고 요약">
        <div className={st.mast}>
          <div className={st.mastMain}>
            <div className={st.mastTitle}>
              <SectionTitle className={st.ellipsis}>{title}</SectionTitle>
              <SourceBadge period={point.period} />
            </div>
            <span className={st.mastMeta} title={metaLine}>
              {metaLine}
            </span>
          </div>
          <FuiButton
            appearance="subtle"
            icon={copied ? <Checkmark16Regular /> : <Copy16Regular />}
            onClick={copySummary}
            aria-label="보고 요약 복사"
          >
            {copied ? "복사됨" : "요약 복사"}
          </FuiButton>
        </div>

        <div className={st.band}>
          <MetricPanel columns={4} ariaLabel="핵심 숫자">
            <Metric
              compact
              label="작업 진척"
              value={m.progress}
              unit="%"
              delta={progressDiff !== null && progressDiff !== 0 ? { text: fmtSignedPp(progressDiff), sense: deltaSense(progressDiff, true), title: "직전 마감 대비" } : undefined}
              caption={m.plannedProgress !== null ? `계획 ${m.plannedProgress}%` : undefined}
              captionInline
              ariaLabel={`작업 진척 ${m.progress}%${m.plannedProgress !== null ? `, 계획 ${m.plannedProgress}%` : ""}${progressDiff !== null && progressDiff !== 0 ? `, 직전 마감 ${fmtSignedPp(progressDiff)}` : ""}`}
            />
            <Metric
              compact
              label="완료 예정"
              value={formatDate(m.projectFinish, "numeric")}
              tone={m.hasBaseline && m.finishVarianceDays > 5 ? "danger" : m.hasBaseline && m.finishVarianceDays > 0 ? "warning" : undefined}
              delta={finishShiftDays !== null && finishShiftDays !== 0 ? { text: fmtDelta(finishShiftDays), sense: deltaSense(finishShiftDays, false), title: "직전 마감 대비" } : undefined}
            />
            <Metric
              compact
              label="계획 비용"
              value={fmtCurrency(m.costTotal, masked)}
              tone={!masked && m.burn > 1 ? "danger" : !masked && m.burn > 0.9 ? "warning" : undefined}
              delta={burnDiffPp !== null && burnDiffPp !== 0 ? { text: fmtSignedPp(burnDiffPp), sense: deltaSense(burnDiffPp, false), title: "직전 마감 대비" } : undefined}
              caption={masked ? (canReveal ? "단가를 숨긴 상태" : "금액 조회 권한이 필요합니다") : undefined}
            />
            <Metric
              compact
              label="확인할 일"
              value={attention}
              unit="건"
              tone={blocked.length > 0 || recSummary.highOpenIssues > 0 ? "danger" : attention > 0 ? "warning" : undefined}
              delta={attentionDiff !== null && attentionDiff !== 0 ? { text: fmtSignedNum(attentionDiff, 0, "건"), sense: deltaSense(attentionDiff, false), title: "직전 마감 대비" } : undefined}
              ariaLabel={`확인할 일 ${attention}건. 이슈 ${openRecords}, 막힌 Task ${blocked.length}, 기한 넘김 ${overdueTasks.length}`}
            />
          </MetricPanel>
        </div>
        <div className={st.band}>
          <CostEvmStrip project={ctx.project} evm={ctx.evm} earnedPercent={ctx.metrics.progress} />
        </div>
        <div className={st.band}>
          <CheckList title="점검" ariaLabel="점검" items={checkItems} />
        </div>
        <IssueTracks records={records} today={recordDay} />
        {point.period?.note && <Body1 className={s.secondary}>메모 · {point.period.note}</Body1>}
      </section>

      <div className={st.pair}>
        <ReportVisual
          className={st.fill}
          title="WBS 단계별 진척 · Task 상태"
          description={phaseLine ?? (m.hasBaseline ? "단계별 실제 진척과 계획 기준선 비교" : "단계별 실제 진척과 실행 Task 구성")}
          help={<PhaseProgressHelp />}
          meta={phaseLine ? undefined : `단계 ${phaseBars.length}개 · Task ${m.execTotal}건`}
          height={Math.max(180, axisBarsHeight(changeBars.length))}
          focusHeight={Math.max(320, axisBarsHeight(phaseBars.length))}
          table={phaseBars.length > 0 ? phaseProgressTable(phaseBars) : undefined}
          chart={
            phaseBars.length > 0 || m.execTotal > 0
              ? () => (
                  <div className={st.stackSection}>
                    {phaseBars.length > 0 && (
                      <PhaseProgressVisual
                        data={phaseBars}
                        ariaLabel={`WBS 단계별 진척: ${phaseBars.map((p) => `${p.label} ${p.actual}%`).join(", ")}`}
                      />
                    )}
                    {m.execTotal > 0 && (
                      <div className={st.subBlock}>
                        <span className={st.subTitle}>실행 Task 상태 분포 ({m.execTotal}건)</span>
                        <StackedBar
                          segments={taskSegments}
                          ariaLabel={`Task 상태: ${taskSegments.map((seg) => `${seg.label} ${seg.value}`).join(", ")}`}
                          onPick={isCurrent && activeId ? (status) => navigate(`/projects/${activeId}/tasks?status=${status}`) : undefined}
                          pickHint="Task 보기"
                        />
                      </div>
                    )}
                  </div>
                )
              : undefined
          }
          footer={
            showMovers && movers ? (
              <PhaseMoveFoot
                gains={movers.gains}
                stalls={movers.stalls}
                gainMore={movers.gainMore}
                stallMore={movers.stallMore}
                openPhase={isCurrent && activeId ? (key) => phaseBars.find((row) => row.key === key)?.onClick?.() : undefined}
              />
            ) : undefined
          }
        />
        <ReportVisual
          className={st.fill}
          title="지난 보고 이후"
          help={<ChangeCategoryHelp />}
          meta={prevPoint ? `${changeTotal}건` : undefined}
          height={axisBarsHeight(changeBars.length)}
          focusHeight={Math.max(320, axisBarsHeight(changeBars.length))}
          table={prevPoint && changeTotal > 0 ? changeTable(counts, (category) => onOpenChanges(category)) : undefined}
          chart={
            prevPoint && changeTotal > 0
              ? (h) => <BarsVisual data={changeBars} height={h} ariaLabel={`지난 보고 이후 변경: ${changeBars.map((bar) => `${bar.label} ${bar.value}건`).join(", ")}`} />
              : undefined
          }
          extra={
            prevPoint && changeTotal > 0 ? (
              <FuiButton appearance="subtle" icon={<ArrowRight16Regular />} iconPosition="after" onClick={() => onOpenChanges()}>
                변경 보기
              </FuiButton>
            ) : undefined
          }
          footer={
            prevPoint && changeTotal > 0 ? (
              <ChangeFoot items={items} masked={masked} openCategory={onOpenChanges} />
            ) : undefined
          }
        >
          <p className={st.emptyLine} title={prevPoint ? "바뀐 곳이 없어요." : "기간을 마감하면 변경이 보여요."}>
            {prevPoint ? "바뀐 곳이 없어요." : "기간을 마감하면 변경이 보여요."}
          </p>
        </ReportVisual>
      </div>

      <div className={st.pair}>
        <ReportVisual
          className={st.fill}
          title="월별 계획 비용"
          description={cost.month.reference ? "점선은 예산을 개월 수로 나눈 값" : "투입·솔루션·경비가 놓인 달"}
          help={<CostMonthHelp even={Boolean(cost.month.reference)} />}
          meta={cost.month.focusLabel}
          height={220}
          focusHeight={360}
          table={cost.month.empty ? undefined : cost.month.table}
          chart={
            cost.month.empty
              ? undefined
              : (h) => (
                  <ColumnsVisual
                    data={cost.month.columns}
                    reference={cost.month.reference}
                    height={h}
                    yMin={0}
                    yMax={cost.month.yTicks.at(-1)}
                    yTicks={cost.month.yTicks}
                    formatY={(v) => formatCostAxis(cost.month.axis, v)}
                    hideBarLabels
                    showCallout
                    showLegend
                    rotateLabels={cost.month.columns.length > 8}
                    ariaLabel={`월별 계획 비용: ${cost.month.columns.map((col) => `${col.calloutTitle ?? col.label} ${col.valueLabel ?? ""}`).join(", ")}`}
                  />
                )
          }
        >
          <p className={st.emptyLine} title="계획 비용이 놓일 기간이 없어요.">
            계획 비용이 놓일 기간이 없어요.
          </p>
        </ReportVisual>
        <ReportVisual
          className={st.fill}
          title="비용 구성"
          description={costLinked ? "막대를 누르면 예산으로 이동" : "계획 비용에서 차지하는 비중"}
          help={<CostMixHelp linked={costLinked} />}
          height={axisBarsHeight(Math.max(cost.bars.length, 1))}
          focusHeight={Math.max(320, axisBarsHeight(cost.bars.length))}
          table={cost.bars.length > 0 ? cost.table : undefined}
          chart={
            cost.bars.length > 0
              ? (h) => <BarsVisual data={cost.bars} height={h} formatX={(v) => `${v}%`} ariaLabel={`비용 구성: ${cost.bars.map((bar) => bar.ariaLabel ?? bar.label).join(", ")}`} />
              : undefined
          }
        >
          <p className={st.emptyLine} title="계획 비용이 없어요.">
            계획 비용이 없어요.
          </p>
        </ReportVisual>
      </div>

      <div className={st.pair}>
        <ReportVisual
          className={st.fill}
          title="마감 분포"
          description={dueView === "weeks" ? matrix.headline || "최근 4주의 요일별 마감" : "기준일에 끝나는 7일 동안 마감이 몰린 날"}
          help={dueView === "weeks" ? <WeekMatrixHelp /> : <WeekDueHelp />}
          meta={dueView === "days" ? week.note : undefined}
          height={220}
          focusHeight={320}
          table={dueView === "weeks" ? matrix.table : week.table}
          extra={
            <SegmentedControl
              ariaLabel="마감 분포 보기"
              value={dueView}
              onChange={setDueView}
              options={[
                { value: "weeks", label: "주" },
                { value: "days", label: "7일" },
              ]}
            />
          }
          chart={
            dueView === "weeks"
              ? matrix.empty
                ? undefined
                : (h) => <HeatmapVisual columns={matrix.columns} rows={matrix.rows} peakColumn={matrix.peakColumn} height={h} ariaLabel={matrix.ariaLabel} />
              : week.empty
                ? undefined
                : (h) => (
                    <ColumnsVisual
                      data={week.columns}
                      reference={week.reference}
                      height={h}
                      yMin={0}
                      yMax={week.yTicks.at(-1)}
                      yTicks={week.yTicks}
                      formatY={(v) => String(Math.round(v))}
                      ariaLabel={week.ariaLabel}
                    />
                  )
          }
        >
          <p className={st.emptyLine} title={dueView === "weeks" ? "최근 4주에 마감인 Task가 없어요." : "기준일에 끝나는 7일에 마감인 Task가 없어요."}>
            {dueView === "weeks" ? "최근 4주에 마감인 Task가 없어요." : "기준일에 끝나는 7일에 마감인 Task가 없어요."}
          </p>
        </ReportVisual>
        <PeriodCard
          tasks={shownPeriodTasks}
          wbsOf={(task) => linkedWbs(task, ctx)}
          openTask={isCurrent && activeId ? (id) => openExecutionTask(activeId, id) : undefined}
          description={dueDay ? `${formatDate(dueDay, "monthDayWeekday")} 마감` : undefined}
          empty={dueDay ? "이 날에 마감인 Task가 없어요." : "이 기간에 마감인 Task가 없어요."}
          extra={
            dueDay ? (
              <FuiButton appearance="subtle" onClick={() => setPickedDay(null)}>
                전체 보기
              </FuiButton>
            ) : undefined
          }
        />
      </div>

      <div ref={boardRef} className={st.board}>
        <ListCard title="확인할 일" help={<p className={st.helpLead}>막힌 Task, 기한을 넘긴 Task, 아직 열린 이슈와 리스크입니다. 왼쪽은 종류, 오른쪽은 마감일입니다.</p>} rows={actionRows} empty="확인할 일이 없어요." jump={jump} />
        <ListCard title="계획보다 늦은 작업" help={<p className={st.helpLead}>저장한 계획보다 끝난 날이 늦은 작업입니다. 왼쪽은 WBS 코드, 오른쪽 숫자는 늦은 날수입니다.</p>} rows={delayRows} empty={m.hasBaseline ? "늦은 작업이 없어요." : "계획을 저장하면 보여요."} jump={jump} />
        <ListCard title="다가오는 마일스톤" help={<p className={st.helpLead}>기준일 앞뒤 7일 안에 있고, 아직 끝나지 않은 이정표입니다. 왼쪽은 WBS 코드, 오른쪽은 그 날입니다.</p>} rows={milestoneRows} empty="남은 마일스톤이 없어요." jump={jump} />
        <ListCard title="담당별 남은 마감" description="이 기간에 마감이고, 아직 끝나지 않은 Task입니다." help={<p className={st.helpLead}>왼쪽은 담당입니다. 이름에 올리면 역할이 보입니다. 숫자는 남은 Task 수이고, 담당이 둘이면 양쪽에 셉니다.</p>} rows={assigneeRows} empty="이 기간에 남은 마감이 없어요." jump={openAssignee} />
      </div>
    </div>
  );
}

function IssueTracks({ records, today }: { records: ProjectRecord[]; today: string }) {
  const st = useStyles();
  const rows = statusIssueTracks(records, today);
  if (rows.length === 0) return null;
  return (
    <div className={st.subBlock}>
      <span className={st.subTitle}>이슈 추적</span>
      {rows.map((row) => (
        <TrackRow
          key={row.id}
          title={row.title}
          opened={formatDate(row.opened, "numeric")}
          state={row.state}
          extent={row.extent}
          comment={row.comment}
          attachments={row.attachments}
        />
      ))}
    </div>
  );
}

function PhaseProgressHelp() {
  const st = useStyles();
  return (
    <div className={st.help}>
      <p className={st.helpLead}>위쪽은 WBS 최상위 단계의 완료율과 계획 기준선 대비 진척이고, 아래쪽은 실행 Task의 상태 비중입니다. 항목을 누르면 해당 일정·Task로 이동합니다.</p>
      <ul className={st.helpList}>
        <li className={st.helpItem}>설명의 앞·뒤는 단계 진척이 가장 높은 값과 가장 낮은 값입니다.</li>
        <li className={st.helpItem}>직전 마감이 있으면, 그 뒤로 진척이 오른 단계와 그대로이거나 줄어든 단계를 나눕니다.</li>
      </ul>
    </div>
  );
}

function PhaseMoveFoot({
  gains,
  stalls,
  gainMore,
  stallMore,
  openPhase,
}: {
  gains: PhaseMover[];
  stalls: PhaseMover[];
  gainMore: number;
  stallMore: number;
  openPhase?: (key: string) => void;
}) {
  const toRows = (rows: PhaseMover[]): SplitRow[] =>
    rows.map((row) => {
      const delta = formatPhasePoints(row.delta);
      return {
        key: row.key,
        label: row.label,
        title: row.wbs ? `${row.wbs} ${row.label}` : row.label,
        value: delta,
        ariaLabel: `${row.label}, ${delta}`,
        onOpen: openPhase ? () => openPhase(row.key) : undefined,
      };
    });
  return (
    <SplitFoot
      title="직전 마감 대비 진척"
      left={{ title: "오른 단계", rows: toRows(gains), moreText: gainMore > 0 ? `외 ${gainMore}단계` : undefined, empty: "없습니다" }}
      right={{ title: "늘지 않은 단계", rows: toRows(stalls), moreText: stallMore > 0 ? `외 ${stallMore}단계` : undefined, empty: "없습니다" }}
    />
  );
}

function changeSpotlightValue(item: ChangeItem, masked: boolean): string {
  if (item.delta !== undefined) return fmtChangeValue(item.delta, masked, true);
  if (item.kind !== "changed") return CHANGE_KIND_LABEL[item.kind];
  return item.field;
}

function ChangeFoot({ items, masked, openCategory }: { items: readonly ChangeItem[]; masked: boolean; openCategory: (category: ChangeCategory) => void }) {
  const spot = changeSpotlights(items, 2);
  const toRows = (rows: readonly ChangeItem[]): SplitRow[] => {
    const values = rows.map((item) => changeSpotlightValue(item, masked));
    return rows.map((item, index) => {
      const plain = values[index] ?? "";
      const collided = rows.some((other, otherIndex) => otherIndex !== index && other.subject === item.subject && values[otherIndex] === plain);
      const value = collided && item.delta !== undefined ? `${plain} · ${item.field}` : plain;
      const title = `${item.code ? `${item.code} ` : ""}${item.subject} · ${item.field}`;
      return {
        key: item.id,
        label: item.subject,
        title,
        value,
        ariaLabel: `${CHANGE_CATEGORY_LABEL[item.category]} ${item.subject}, ${value}`,
        onOpen: () => openCategory(item.category),
      };
    });
  };
  return (
    <SplitFoot
      title="주요 변경"
      left={{ title: "확인할 변경", rows: toRows(spot.attention), moreText: spot.attentionMore > 0 ? `외 ${spot.attentionMore}건` : undefined, moreInline: true, empty: "없습니다" }}
      right={{ title: "그 외 변경", rows: toRows(spot.rest), moreText: spot.restMore > 0 ? `외 ${spot.restMore}건` : undefined, moreInline: true, empty: "없습니다" }}
    />
  );
}

interface SplitRow {
  key: string;
  label: string;
  title: string;
  value: string;
  ariaLabel: string;
  onOpen?: () => void;
}

function SplitFoot({
  title,
  left,
  right,
}: {
  title: string;
  left: { title: string; rows: SplitRow[]; moreText?: string; moreInline?: boolean; empty: string };
  right: { title: string; rows: SplitRow[]; moreText?: string; moreInline?: boolean; empty: string };
}) {
  const st = useStyles();
  return (
    <div className={st.subBlock}>
      <span className={st.subTitle}>{title}</span>
      <div className={st.movers}>
        <SplitColumn {...left} />
        <SplitColumn {...right} />
      </div>
    </div>
  );
}

function SplitColumn({ title, rows, moreText, moreInline = false, empty }: { title: string; rows: SplitRow[]; moreText?: string; moreInline?: boolean; empty: string }) {
  const s = useReportStyles();
  const st = useStyles();
  const head = moreInline ? (
    <div className={st.moverHeadLine}>
      <span className={mergeClasses(st.moverHead, st.moverHeadGrow)} title={title}>
        {title}
      </span>
      {moreText && <span className={st.moverHead}>{moreText}</span>}
    </div>
  ) : (
    <span className={st.moverHead} title={title}>
      {title}
    </span>
  );
  return (
    <div className={st.moverCol}>
      {head}
      {rows.length === 0 ? (
        <p className={st.emptyLine} title={empty}>
          {empty}
        </p>
      ) : (
        <div role="list" aria-label={title}>
          {rows.map((row) => {
            const content = (
              <>
                <span className={s.truncate} title={row.title}>
                  {row.label}
                </span>
                <span className={s.num}>{row.value}</span>
              </>
            );
            return row.onOpen ? (
              <button key={row.key} type="button" role="listitem" className={mergeClasses(st.moverRow, st.moverButton)} aria-label={row.ariaLabel} onClick={row.onOpen}>
                {content}
              </button>
            ) : (
              <div key={row.key} role="listitem" className={st.moverRow}>
                {content}
              </div>
            );
          })}
        </div>
      )}
      {!moreInline && moreText && <span className={st.moverHead}>{moreText}</span>}
    </div>
  );
}

function WeekMatrixHelp() {
  const st = useStyles();
  return (
    <div className={st.help}>
      <p className={st.helpLead}>최근 4주를 요일로 펼친 마감 수입니다. 칸을 누르면 그 날만 봅니다.</p>
      <ul className={st.helpList}>
        <li className={st.helpItem}>색이 진할수록 그 그림 안에서 마감이 많습니다. 테두리가 있는 칸이 기준일입니다.</li>
        <li className={st.helpItem}>보고 기간 밖은 빈칸입니다. 0은 그 날에 마감이 없는 것입니다.</li>
      </ul>
    </div>
  );
}

function WeekDueHelp() {
  const st = useStyles();
  return (
    <div className={st.help}>
      <p className={st.helpLead}>기준일까지 7일 동안 마감인 Task 수입니다. 막대를 누르면 그 날만 봅니다.</p>
      <ul className={st.helpList}>
        <li className={st.helpItem}>점선은 이 7일의 하루 평균입니다.</li>
        <li className={st.helpItem}>마감 건수가 20% 안인 구간이 3개 이상이면, 직전 7일을 뺀 그 평균으로 바뀝니다.</li>
      </ul>
    </div>
  );
}

function CostMonthHelp({ even }: { even: boolean }) {
  const st = useStyles();
  return (
    <div className={st.help}>
      <p className={st.helpLead}>투입, 솔루션, 경비가 각 달에 놓인 계획 비용입니다. 경비는 이 기간의 달에 고르게 나뉩니다.</p>
      <ul className={st.helpList}>
        <li className={st.helpItem}>진한 막대는 진척 기준일이 있는 달입니다.</li>
        {even && <li className={st.helpItem}>점선은 승인 예산을 개월 수로 나눈 기준입니다.</li>}
      </ul>
    </div>
  );
}

function CostMixHelp({ linked }: { linked: boolean }) {
  const st = useStyles();
  return (
    <div className={st.help}>
      <p className={st.helpLead}>직접인건비, 제경비, 기술료, 솔루션·라이선스, 직접경비 가운데 금액이 있는 항목입니다. 길이는 합계에서의 비중입니다.</p>
      {linked && (
        <ul className={st.helpList}>
          <li className={st.helpItem}>항목을 누르면 예산의 그 보기로 이동합니다.</li>
        </ul>
      )}
    </div>
  );
}

function ChangeCategoryHelp() {
  const st = useStyles();
  return (
    <div className={st.help}>
      <p className={st.helpLead}>지난 마감과 지금을 견준 변경 건수입니다. 아래는 그 가운데 먼저 볼 변경입니다.</p>
      <ul className={st.helpList}>
        {CHANGE_CATEGORIES.map((category) => (
          <li key={category} className={st.helpItem}>
            {CHANGE_CATEGORY_LABEL[category]} — {CHANGE_CATEGORY_HINT[category]}
          </li>
        ))}
        <li className={st.helpItem}>확인할 변경은 늦어지거나 줄어든 것과 삭제입니다. 이름을 누르면 그 구분의 변경 내역으로 이동합니다.</li>
      </ul>
    </div>
  );
}

/** 카드의 내용 높이. 여섯 줄을 넘는 펼침은 높이 맞춤에 넣지 않는다. */
function contentBoxHeight(el: HTMLElement) {
  const cs = getComputedStyle(el);
  const pad = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
  const border = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
  const gap = cs.rowGap === "normal" ? 0 : parseFloat(cs.rowGap) || 0;
  const kids = [...el.children] as HTMLElement[];
  const inner = kids.reduce((sum, kid) => sum + cappedHeight(kid), 0) + gap * Math.max(0, kids.length - 1);
  return Math.ceil(inner + pad + border);
}

function cappedHeight(el: HTMLElement) {
  const items = [...el.children].filter((kid) => kid.getAttribute("role") === "listitem") as HTMLElement[];
  if (items.length <= 6) return el.offsetHeight;
  const rest = [...el.children].filter((kid) => kid.getAttribute("role") !== "listitem") as HTMLElement[];
  return [...items.slice(0, 6), ...rest].reduce((sum, kid) => sum + kid.offsetHeight, 0);
}

/** 두 열일 때 보드의 카드 높이를 가장 긴 칸에 맞춘다. 목록을 펼쳐도 다른 칸은 따라 늘지 않는다. */
function useEqualCardHeight(rootRef: RefObject<HTMLDivElement | null>, key: string) {
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const mq = window.matchMedia("(min-width: 961px)");
    const cards = () => [...root.children] as HTMLElement[];
    const apply = () => {
      const list = cards();
      if (!mq.matches || list.length === 0) {
        for (const card of list) card.style.minHeight = "";
        return;
      }
      const next = `${Math.max(...list.map(contentBoxHeight))}px`;
      for (const card of list) {
        if (card.style.minHeight !== next) card.style.minHeight = next;
      }
    };
    apply();
    window.addEventListener("resize", apply);
    mq.addEventListener("change", apply);
    return () => {
      window.removeEventListener("resize", apply);
      mq.removeEventListener("change", apply);
      for (const card of cards()) card.style.minHeight = "";
    };
  }, [rootRef, key]);
}

function ListCard({ title, description, help, rows, empty, jump, limit = 6 }: { title: string; description?: string; help?: ReactNode; rows: Row[]; empty: string; jump: (row: Row) => (() => void) | undefined; limit?: number }) {
  const s = useReportStyles();
  const st = useStyles();
  const [all, setAll] = useState(false);
  const shown = all ? rows : rows.slice(0, limit);
  return (
    <ReportVisual className={st.fill} title={title} description={description} help={help}>
      {rows.length === 0 ? (
        <p className={st.emptyLine} title={empty}>
          {empty}
        </p>
      ) : (
        <div className={st.list} role="list">
          {shown.map((r) => {
            const onClick = jump(r);
            const content = (
              <>
                {r.leading ?? (r.tag ? (
                  <span className={s.code} title={r.tag}>
                    {r.tag}
                  </span>
                ) : (
                  <span />
                ))}
                <span className={s.truncate} title={r.hint ? `${r.name} · ${r.hint}` : r.name}>
                  {r.name}
                </span>
                <span className={s.num}>{r.value}</span>
              </>
            );
            return onClick ? (
              <button key={r.key} type="button" role="listitem" className={mergeClasses(s.listRow, s.listRowButton)} onClick={onClick}>
                {content}
              </button>
            ) : (
              <div key={r.key} role="listitem" className={s.listRow}>
                {content}
              </div>
            );
          })}
          {rows.length > limit && (
            <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: tokens.spacingVerticalXXS }}>
              <FuiButton appearance="subtle" onClick={() => setAll(!all)}>
                {all ? "접기" : `${rows.length - limit}개 더 보기`}
              </FuiButton>
            </div>
          )}
        </div>
      )}
    </ReportVisual>
  );
}

function PeriodHelp() {
  const st = useStyles();
  return (
    <div className={st.help}>
      <p className={st.helpLead}>지난 마감 다음 날부터 기준일까지 마감일이 있는 Task입니다. 끝난 Task도 포함합니다.</p>
      <ul className={st.helpList}>
        <li className={st.helpItem}>왼쪽 숫자는 연결된 WBS 코드입니다.</li>
        <li className={st.helpItem}>날짜는 시작일–마감일입니다. 마감일만 있으면 그 날만 보입니다.</li>
      </ul>
    </div>
  );
}

function linkedWbs(task: ExecutionTask, ctx: { schedule: { tasks: { get: (id: string) => { wbs: string } | undefined } }; project: { tasks: { id: string; name: string }[] } }): { text: string; title: string } | null {
  const hits = task.linkedTaskIds.flatMap((id) => {
    const node = ctx.schedule.tasks.get(id);
    if (!node) return [];
    const name = ctx.project.tasks.find((item) => item.id === id)?.name;
    return [name ? `${node.wbs} ${name}` : node.wbs];
  });
  const wbs = task.linkedTaskIds.map((id) => ctx.schedule.tasks.get(id)?.wbs).find((code) => !!code);
  if (!wbs) return null;
  return { text: wbs, title: hits.join(", ") };
}

function taskSpan(task: ExecutionTask): { text: string; title: string } {
  if (task.start && task.due) {
    return {
      text: formatDateRange(task.start, task.due, "short"),
      title: `${formatDate(task.start, "full")} 시작, ${formatDate(task.due, "full")} 마감`,
    };
  }
  if (task.due) return { text: formatDate(task.due, "short"), title: `${formatDate(task.due, "full")} 마감` };
  if (task.start) return { text: formatDate(task.start, "short"), title: `${formatDate(task.start, "full")} 시작` };
  return { text: "날짜 없음", title: "시작일과 마감일이 없습니다" };
}

function PeriodCard({ tasks, wbsOf, openTask, description, empty = "이 기간에 마감인 Task가 없어요.", extra, limit = 6 }: { tasks: ExecutionTask[]; wbsOf: (task: ExecutionTask) => { text: string; title: string } | null; openTask?: (id: string) => void; description?: string; empty?: string; extra?: ReactNode; limit?: number }) {
  const s = useReportStyles();
  const st = useStyles();
  const [all, setAll] = useState(false);
  const shown = all ? tasks : tasks.slice(0, limit);
  return (
    <ReportVisual className={st.fill} title="이 기간 마감 Task" description={description} help={<PeriodHelp />} extra={extra}>
      {tasks.length === 0 ? (
        <p className={st.emptyLine} title={empty}>
          {empty}
        </p>
      ) : (
        <div className={st.list} role="list">
          {shown.map((task) => {
            const open = openTask ? () => openTask(task.id) : undefined;
            const name = task.title || "제목 없는 Task";
            const who = task.assignees.length > 0 ? task.assignees.join(", ") : "담당 미정";
            const code = wbsOf(task);
            const when = taskSpan(task);
            const status = task.status ? STATUS_LABEL[task.status] : "미정";
            const content = (
              <>
                {code ? (
                  <span className={s.code} title={code.title}>
                    {code.text}
                  </span>
                ) : (
                  <span />
                )}
                <span className={s.truncate} title={name}>
                  {name}
                </span>
                <span className={st.periodMeta}>
                  <span className={s.code} title={status}>
                    {status}
                  </span>
                  <span title={who}>
                    <AssigneeAvatars names={task.assignees} size={20} />
                  </span>
                  <span className={st.spanDate} title={when.title}>
                    {when.text}
                  </span>
                </span>
              </>
            );
            return open ? (
              <button key={task.id} type="button" role="listitem" className={mergeClasses(s.listRow, s.listRowButton)} onClick={open}>
                {content}
              </button>
            ) : (
              <div key={task.id} role="listitem" className={s.listRow}>
                {content}
              </div>
            );
          })}
          {tasks.length > limit && (
            <div style={{ display: "flex", justifyContent: "flex-end", paddingTop: tokens.spacingVerticalXXS }}>
              <FuiButton appearance="subtle" onClick={() => setAll(!all)}>
                {all ? "접기" : `${tasks.length - limit}개 더 보기`}
              </FuiButton>
            </div>
          )}
        </div>
      )}
    </ReportVisual>
  );
}
